/**
 * EditableCreatorsSection - Join The Movement section with element-level editing
 * 
 * Each element (heading, subheading, CTA button) is wrapped with SelectableElement
 * for click-to-select in the Page Builder Studio.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import SelectableElement from './SelectableElement';
import InlineTextEditor from './InlineTextEditor';
import { 
  LANDING_CREATORS_ELEMENTS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Element animation wrapper
const AnimatedElement = ({ 
  children, 
  motion,
  animationKey,
  disabled,
  className = '' 
}) => {
  const ref = useRef(null);
  const prevKeyRef = useRef(animationKey);
  
  const { durationMs = 700, delayMs = 0, easing = 'smooth', fromOpacity = 0, fromY = 30, fromScale = 1 } = motion || {};
  const easingValue = EASING_VALUES[easing] || EASING_VALUES.smooth;

  useEffect(() => {
    if (animationKey !== prevKeyRef.current && ref.current) {
      prevKeyRef.current = animationKey;
      ref.current.style.opacity = fromOpacity;
      ref.current.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
      void ref.current.offsetWidth;
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0) scale(1)';
        }
      }, 50);
    }
  }, [animationKey, fromOpacity, fromY, fromScale]);

  useEffect(() => {
    if (disabled || !ref.current) return;
    const el = ref.current;
    el.style.opacity = fromOpacity;
    el.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
    el.style.transition = `opacity ${durationMs}ms ${easingValue} ${delayMs}ms, transform ${durationMs}ms ${easingValue} ${delayMs}ms`;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, durationMs, delayMs, easingValue, fromOpacity, fromY, fromScale]);

  if (disabled) return <div className={className}>{children}</div>;
  return <div ref={ref} className={className}>{children}</div>;
};

const getTextStyleClass = (styles) => {
  const sizeMap = {
    'xs': 'text-xs', 'sm': 'text-sm', 'base': 'text-base', 'lg': 'text-lg',
    'xl': 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl',
  };
  const weightMap = {
    'light': 'font-light', 'normal': 'font-normal', 'medium': 'font-medium',
    'semibold': 'font-semibold', 'bold': 'font-bold',
  };
  const alignMap = { 'left': 'text-left', 'center': 'text-center', 'right': 'text-right' };
  
  return `${sizeMap[styles?.size] || 'text-base'} ${weightMap[styles?.weight] || ''} ${alignMap[styles?.alignment] || 'text-center'}`;
};

const getButtonVariantClass = (styles) => {
  const variants = {
    primary: 'bg-orange-500 hover:bg-orange-600 text-white',
    secondary: 'bg-zinc-800 hover:bg-zinc-700 text-white',
    outline: 'border-white/30 text-white hover:bg-white/10 bg-transparent',
    ghost: 'text-white hover:bg-white/10 bg-transparent',
  };
  const paddings = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-6 text-lg',
  };
  const radii = {
    none: 'rounded-none',
    sm: 'rounded',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    full: 'rounded-full',
  };
  
  return `${variants[styles?.variant] || variants.primary} ${paddings[styles?.padding] || paddings.md} ${radii[styles?.radius] || radii.md}`;
};

const EditableCreatorsSection = ({ 
  props = {},
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  animationKey = 0,
  disabled = false,
}) => {
  const elements = useMemo(() => {
    const map = {};
    LANDING_CREATORS_ELEMENTS.forEach(el => { map[el.id] = el; });
    return map;
  }, []);

  const getValue = (elementId) => {
    const element = elements[elementId];
    if (!element) return '';
    return getElementValue(props, element);
  };

  const getStyles = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementStyles(props, element);
  };

  const getMotion = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementMotion(props, element);
  };

  const handleSelectElement = (elementId) => onSelectElement?.(elementId);
  const handleDoubleClick = (elementId) => onStartEditing?.(elementId);
  const handleSaveText = (elementId, newValue) => {
    onUpdateElementValue?.(elementId, newValue);
    onStopEditing?.();
  };

  // Safe string coercion - ensures we never render objects as React children
  const safeString = (val, fallback = '') => {
    if (!val) return fallback;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      if (val.title) return val.title;
      if (val.label) return val.label;
      if (val.text) return val.text;
    }
    return fallback;
  };

  // Heading
  const headingValue = safeString(getValue('creators.heading'), 'Join the movement');
  const headingStyles = getStyles('creators.heading');
  const headingMotion = getMotion('creators.heading');

  // Subheading
  const subheadingValue = safeString(getValue('creators.subheading'), "Trap Comix is built by and for creators who've been underserved by mainstream platforms.");
  const subheadingStyles = getStyles('creators.subheading');
  const subheadingMotion = getMotion('creators.subheading');

  // CTA
  const ctaValue = getValue('creators.cta');
  const ctaStyles = getStyles('creators.cta');
  const ctaMotion = getMotion('creators.cta');
  const ctaLabel = typeof ctaValue === 'object' ? ctaValue.label : (ctaValue || props.ctaLabel || 'Start Creating');

  // Creator types
  const creatorTypes = props.creatorTypes || ['Comic Artists', 'Writers', 'Novelists', 'Indie Publishers'];

  return (
    <section className="py-24 bg-gradient-to-b from-black to-zinc-950" data-testid="creators-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        {/* Heading */}
        <AnimatedElement
          motion={headingMotion}
          animationKey={animationKey}
          disabled={disabled}
        >
          <SelectableElement
            elementId="creators.heading"
            elementLabel="Section Heading"
            elementType="text"
            isSelected={selectedElementId === 'creators.heading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <h2 
              className={`text-white mb-4 ${getTextStyleClass(headingStyles)}`}
              style={{ opacity: headingStyles.opacity ?? 1 }}
              onDoubleClick={() => handleDoubleClick('creators.heading')}
            >
              {editingElementId === 'creators.heading' ? (
                <InlineTextEditor
                  value={headingValue}
                  isEditing={true}
                  maxLength={60}
                  onSave={(val) => handleSaveText('creators.heading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                headingValue
              )}
            </h2>
          </SelectableElement>
        </AnimatedElement>

        {/* Subheading */}
        <AnimatedElement
          motion={subheadingMotion}
          animationKey={animationKey}
          disabled={disabled}
          className="mb-8"
        >
          <SelectableElement
            elementId="creators.subheading"
            elementLabel="Subheading"
            elementType="text"
            isSelected={selectedElementId === 'creators.subheading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <p 
              className={`text-zinc-400 max-w-2xl mx-auto ${getTextStyleClass(subheadingStyles)}`}
              style={{ opacity: subheadingStyles.opacity ?? 0.7 }}
              onDoubleClick={() => handleDoubleClick('creators.subheading')}
            >
              {editingElementId === 'creators.subheading' ? (
                <InlineTextEditor
                  value={subheadingValue}
                  isEditing={true}
                  maxLength={200}
                  onSave={(val) => handleSaveText('creators.subheading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                subheadingValue
              )}
            </p>
          </SelectableElement>
        </AnimatedElement>
        
        {/* Creator Type Tags */}
        <AnimatedElement
          motion={{ ...subheadingMotion, delayMs: (subheadingMotion.delayMs || 0) + 100 }}
          animationKey={animationKey}
          disabled={disabled}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          {creatorTypes.map((type, index) => (
            <span 
              key={index}
              className="px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-full text-zinc-300 text-sm"
            >
              {type}
            </span>
          ))}
        </AnimatedElement>
        
        {/* CTA Button */}
        <AnimatedElement
          motion={ctaMotion}
          animationKey={animationKey}
          disabled={disabled}
        >
          <SelectableElement
            elementId="creators.cta"
            elementLabel="CTA Button"
            elementType="button"
            isSelected={selectedElementId === 'creators.cta'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <Button 
              size="lg" 
              className={getButtonVariantClass(ctaStyles)}
            >
              {ctaLabel}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </SelectableElement>
        </AnimatedElement>
      </div>
    </section>
  );
};

export default EditableCreatorsSection;
